import Service, { inject as service } from '@ember/service';
import { task } from 'ember-concurrency';
import { bind } from '@ember/runloop';
import { A } from '@ember/array';
import { readOnly } from '@ember/object/computed';
import RetryingPromise from 'sh-www/sh-custom-objects/retrying-promise';

export default Service.extend({
  /**
   * Register services
   */
  applicationState: service('states/application'),
  applicationStateManager: service('state-managers/application'),
  session: service(),
  store: service(),
  router: service(),
  threadManager: service('managers/thread-manager'),

  currentUser: readOnly('session.currentUser'),

  /////////////////////////////////
  //  The Actual Recent Threads State Properties
  /////////////////////////////////

  /**
   * Note - This is now aliased to the global app state since it is shared in other parts of the app now.
   *
   * @type {Ember.A<Thread>}
   */
  mostRecentInboxThreads: readOnly('applicationState.mostRecentInboxThreads'),
  mostRecentArchiveThreads: A(),

  mostRecentInboxThreadsGross: A(),
  mostRecentArchiveThreadsGross: A(),

  /////////////////////////////////
  /////////////////////////////////

  mostRecentThreadsMax: 10,
  mostRecentThreadsGrossMax: 50,

  messageCenterActive: false,

  /////////////////////////////////
  //  updateMostRecentThreads()
  //
  // * Retrieves the most recent threads from the server and
  // * sets the mostRecentThreads property with the response
  /////////////////////////////////

  updateMostRecentThreads(shouldPullInbox = true, shouldPullArchive = false) {
    let threadsPromise = this._threadsQuery();

    let messageCenterActive = this.get('messageCenterActive');

    if (shouldPullInbox) {
      threadsPromise.then(bind(this, this._initializeAndSetThreadsGenerator(messageCenterActive)));
    }

    if (messageCenterActive && shouldPullArchive) {
      let archivedThreadsPromise = this._threadsQuery(1);
      archivedThreadsPromise.then(bind(this,
        this._initializeAndSetThreadsGenerator(messageCenterActive, 'archive')));
    }
  },

  /////////////////////////////////
  //  _initializeAndSetThreadsGenerator(messageCenterActive = false, type = 'inbox')
  //
  // * Returns the appropriate .then() function for thread processing depending
  // * upon whether Message Center is open and the thread type.
  /////////////////////////////////

  _initializeAndSetThreadsGenerator(messageCenterActive = false, type = 'inbox') {

    return (threads) => {
      if (threads && (typeof threads.toArray === 'function')) {
        let threadManager = this.get('threadManager');
        let currentUser = this.get('currentUser');

        threads = A(threads.toArray());

        threads.forEach((thread) => {
          threadManager.initializeThreadPseudoProperties(thread, currentUser);
        });

        if (!messageCenterActive) {
          this.get('applicationStateManager').updateRecentInboxThreads(threads);
        } else if (type === 'inbox') {
          let mostRecentThreads = A(threads.slice(0, this.get('mostRecentThreadsMax')));
          this.get('applicationStateManager').updateRecentInboxThreads(mostRecentThreads);
          this.set('mostRecentInboxThreadsGross', threads);
        } else {
          this.set('mostRecentArchiveThreads', A(threads.slice(0, this.get('mostRecentThreadsMax'))));
          this.set('mostRecentArchiveThreadsGross', threads);
        }
      }
    };

  },


  /////////////////////////////////
  //  _threadsQuery
  //
  // * Generates the appropriate query method for store.query
  // * and returns an un-"then"-ed Retrying Promise
  /////////////////////////////////

  _threadsQuery(archived, updatedAt, page) {
    page = (typeof page !== 'undefined') ? page : 1;
    archived = (typeof archived !== 'undefined') ? archived : 0;

    const include = 'messages.actions,messages.user,users,users.company,all-time-users,assignment,messages.assets.uri-collection';
    const count = this.messageCenterActive ? this.mostRecentThreadsGrossMax : this.mostRecentThreadsMax;

    // Build the filter

    const filter = {
      user_id: this.currentUser.id,
      archived: {
        user_id: this.currentUser.id,
        value: archived
      }
    };

    if (updatedAt) {
      filter.updatedAt = updatedAt;
    }

    // Return the query

    return RetryingPromise.create().generate(() => {
      return this.store.query('thread', {
        filter,
        page,
        count,
        include,
      });
    });
  },

  start() {
    this.startPolling.perform();
  },

  startPolling: task(function* () {
    this.updateMostRecentThreads();
  }).drop(),
});
